import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { FiCheckCircle, FiDownload, FiShoppingBag, FiBookOpen, FiMessageCircle, FiMail, FiTag, FiFileText, FiGrid } from 'react-icons/fi';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { clearSavedCart } from './courseCatalogHelpers';
import { Container, PageStack, Card, Hero, HeroIcon, Headline, HeroSubtitle, EmailNotice, Badge, WhatsAppCard, CardTitle, MutedText, WhatsAppList, CourseJoinRow, CourseJoinName, JoinButton, SummaryHeader, MetadataGrid, MetadataTile, TileLabel, TileValue, PromoPill, LineItems, LineRow, LineLabel, ItemDetails, ItemName, ItemMeta, LineValue, TotalRow, TotalValue, ButtonGroup, ActionButton } from './PurchaseConfirmation.styles';

const formatCurrency = (amount) => `$${parseFloat(amount || 0).toFixed(2)}`;

const formatCourseDate = (dateString) => {
  if (!dateString) return null;

  const datePart = String(dateString).split('T')[0];
  const [year, month, day] = datePart.split('-');
  if (!year || !month || !day) return null;

  const date = new Date(parseInt(year, 10), parseInt(month, 10) - 1, parseInt(day, 10));
  if (Number.isNaN(date.getTime())) return null;

  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const getCourseDateRange = (course) => {
  const startDate = formatCourseDate(course.startDate || course.start_date);
  const endDate = formatCourseDate(course.endDate || course.end_date);

  if (startDate && endDate) return `${startDate} - ${endDate}`;
  if (startDate) return `Starts ${startDate}`;
  if (endDate) return `Ends ${endDate}`;
  return null;
};

const getInstructorName = (course) => {
  const directInstructor = course.instructor || course.instructorName || course.instructor_name;
  if (directInstructor) return directInstructor;

  return `${course.instructorFirstName || course.instructor_first_name || ''} ${course.instructorLastName || course.instructor_last_name || ''}`.trim();
};

function PurchaseConfirmation() {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [purchaseData, setPurchaseData] = useState(location.state?.purchaseData);
  const [loading, setLoading] = useState(!purchaseData);
  const [error, setError] = useState(null);
  const hasFetchedRef = useRef(false);
  const { user: sessionUser } = useAuth();

  // The purchase went through, so the catalog cart that led here is spent.
  useEffect(() => {
    if (purchaseData) clearSavedCart();
  }, [purchaseData]);

  useEffect(() => {
    const scrollToTop = () => {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    };

    scrollToTop();
    const frameId = requestAnimationFrame(scrollToTop);
    const timeoutId = window.setTimeout(scrollToTop, 50);

    return () => {
      cancelAnimationFrame(frameId);
      window.clearTimeout(timeoutId);
    };
  }, [location.pathname]);

  useEffect(() => {
    if (!purchaseData && !hasFetchedRef.current) {
      const paymentIntentId = searchParams.get('payment_intent');

      if (paymentIntentId) {
        hasFetchedRef.current = true;
        setLoading(true);
        setError(null);

        const redirectStatus = searchParams.get('redirect_status');
        if (redirectStatus === 'failed') {
          setError('Payment was declined. Please try a different payment method or contact your bank.');
          setLoading(false);
          return;
        }

        api.getPurchaseConfirmation(paymentIntentId)
          .then((data) => {
            if (data.error) {
              setError(data.message || 'Payment was not successful. Please try again.');
              setLoading(false);
            } else {
              setPurchaseData(data);
              setLoading(false);
            }
          })
          .catch((err) => {
            console.error('Failed to fetch purchase confirmation:', err);
            const errorMessage = err.response?.data?.message || err.message || 'Failed to load purchase confirmation';
            const paymentStatus = err.response?.data?.paymentStatus;

            if (paymentStatus === 'failed' || errorMessage.includes('declined') || errorMessage.includes('failed')) {
              setError('Payment was declined. Please try a different payment method or contact your bank.');
            } else {
              setError(errorMessage);
            }
            setLoading(false);
            hasFetchedRef.current = false;
          });
      } else {
        setLoading(false);
      }
    }
  }, [purchaseData, searchParams]);

  if (loading) {
    return (
      <Container>
        <PageStack initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <Hero>
            <HeroSubtitle>Loading purchase confirmation...</HeroSubtitle>
          </Hero>
        </PageStack>
      </Container>
    );
  }

  if (error || !purchaseData) {
    return (
      <Container>
        <PageStack initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <Hero>
            <HeroSubtitle>{error || 'No purchase data found. Please try again.'}</HeroSubtitle>
          </Hero>
          <ButtonGroup>
            <ActionButton $primary onClick={() => navigate('/catalog')}>
              <FiShoppingBag size={16} />
              Browse course catalog
            </ActionButton>
          </ButtonGroup>
        </PageStack>
      </Container>
    );
  }

  if (!purchaseData.user || !purchaseData.payment) {
    return (
      <Container>
        <PageStack initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <Hero>
            <HeroSubtitle>Purchase data is incomplete. Please contact support if this issue persists.</HeroSubtitle>
          </Hero>
          <ButtonGroup>
            <ActionButton $primary onClick={() => navigate('/catalog')}>
              <FiShoppingBag size={16} />
              Browse course catalog
            </ActionButton>
          </ButtonGroup>
        </PageStack>
      </Container>
    );
  }

  const { user, products, courses, payment, promoCode } = purchaseData;
  const totalAmount = parseFloat(payment.amount || 0);
  const isCourseEnrollment = courses && courses.length > 0;
  const items = isCourseEnrollment ? courses : products || [];
  const paymentStatus = String(payment.status || purchaseData.status || '').toLowerCase();
  const isFreeEnrollment = purchaseData.freeEnrollment === true || totalAmount === 0;
  const hasSuccessfulPayment = ['succeeded', 'success', 'completed', 'complete', 'paid'].includes(paymentStatus);
  const canShowWhatsAppLinks = isCourseEnrollment && (hasSuccessfulPayment || isFreeEnrollment);
  const coursesWithWhatsAppLinks = canShowWhatsAppLinks
    ? courses.filter(course => {
        const link = course.whatsappGroupLink || course.whatsapp_group_link;
        return typeof link === 'string' && link.trim().length > 0;
      })
    : [];

  const customerName = `${user.firstName || user.first_name || ''} ${user.lastName || user.last_name || ''}`.trim();
  const primaryCourseName = isCourseEnrollment
    ? courses.length === 1 ? courses[0].name : `${courses.length} courses`
    : items.length === 1 ? items[0].name : `${items.length} items`;
  const orderNumber = payment.orderNumber || (totalAmount === 0 ? 'Completed' : 'Pending');
  const purchaseDate = payment.purchaseDate ? new Date(payment.purchaseDate).toLocaleDateString() : new Date().toLocaleDateString();
  const originalSubtotal = items.reduce((sum, item) => sum + (parseFloat(item.price) || 0), 0);
  const discountAmount = Math.max(0, originalSubtotal - totalAmount);
  const hasDiscount = discountAmount > 0.01;

  const handleDownloadReceipt = () => {
    if (payment.receiptUrl) {
      window.open(payment.receiptUrl, '_blank');
    }
  };

  return (
    <Container>
      <PageStack initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
        <Hero>
          <HeroIcon>
            <FiCheckCircle size={28} />
          </HeroIcon>
          <Headline>You're enrolled!</Headline>
          <HeroSubtitle>{primaryCourseName} · Order {orderNumber}</HeroSubtitle>
        </Hero>

        <EmailNotice>
          <FiMail size={16} />
          <span>Confirmation sent to <strong>{user.email || 'your email address'}</strong></span>
        </EmailNotice>

        {Boolean(sessionUser?.password_change_required) && (
          <Card>
            <CardTitle>One last step: set your password</CardTitle>
            <MutedText>
              Your enrollment is confirmed. Choose a password to activate your account and open your courses.
            </MutedText>
            <ButtonGroup>
              <ActionButton $primary onClick={() => navigate('/dashboard')}>
                Set my password
              </ActionButton>
            </ButtonGroup>
          </Card>
        )}

        {coursesWithWhatsAppLinks.length > 0 && (
          <WhatsAppCard>
            <Badge>
              <FiMessageCircle size={13} />
              Next step
            </Badge>
            <div>
              <CardTitle>Join your course group</CardTitle>
              <MutedText>Please join the WhatsApp group for each enrolled course to receive important class updates and announcements.</MutedText>
            </div>
            <WhatsAppList>
              {coursesWithWhatsAppLinks.map(course => {
                const link = (course.whatsappGroupLink || course.whatsapp_group_link).trim();
                return (
                  <CourseJoinRow key={`${course.id}-${course.code}`} href={link} target="_blank" rel="noopener noreferrer">
                    <CourseJoinName>{course.name}</CourseJoinName>
                    <JoinButton><FiMessageCircle size={14} /> Join</JoinButton>
                  </CourseJoinRow>
                );
              })}
            </WhatsAppList>
          </WhatsAppCard>
        )}

        <Card>
          <SummaryHeader>
            <FiFileText size={13} />
            Order summary
          </SummaryHeader>
          <MetadataGrid>
            <MetadataTile>
              <TileLabel>Order no.</TileLabel>
              <TileValue>{orderNumber}</TileValue>
            </MetadataTile>
            <MetadataTile>
              <TileLabel>Date</TileLabel>
              <TileValue>{purchaseDate}</TileValue>
            </MetadataTile>
            <MetadataTile>
              <TileLabel>Customer</TileLabel>
              <TileValue>{customerName || 'N/A'}</TileValue>
            </MetadataTile>
            <MetadataTile>
              <TileLabel>Promo code</TileLabel>
              <TileValue>
                {promoCode ? (
                  <PromoPill>
                    <FiTag size={13} />
                    {promoCode.code}
                  </PromoPill>
                ) : 'None'}
              </TileValue>
            </MetadataTile>
          </MetadataGrid>

          <LineItems>
            {items.map((item, index) => {
              const instructorName = isCourseEnrollment ? getInstructorName(item) : null;
              const courseDateRange = isCourseEnrollment ? getCourseDateRange(item) : null;

              return (
                <LineRow key={`${item.id || index}-${item.code || item.name}`}>
                  <LineLabel>
                    <ItemDetails>
                      <ItemName>{item.name}</ItemName>
                      {isCourseEnrollment && (item.code || instructorName) && (
                        <ItemMeta>{`${item.code || 'N/A'} • ${instructorName || 'TBA'}`}</ItemMeta>
                      )}
                      {courseDateRange && <ItemMeta>{courseDateRange}</ItemMeta>}
                    </ItemDetails>
                  </LineLabel>
                  <LineValue $accent={parseFloat(item.price || 0) === 0}>
                    {parseFloat(item.price || 0) === 0 ? 'Free' : formatCurrency(item.price)}
                  </LineValue>
                </LineRow>
              );
            })}

            {hasDiscount && (
              <LineRow>
                <LineLabel>Discount{promoCode ? ` (${promoCode.code})` : ''}</LineLabel>
                <LineValue $accent>-{formatCurrency(discountAmount)}</LineValue>
              </LineRow>
            )}

            <TotalRow>
              <LineLabel>Total</LineLabel>
              <TotalValue>{totalAmount === 0 ? 'Free' : formatCurrency(totalAmount)}</TotalValue>
            </TotalRow>
          </LineItems>
        </Card>

        <ButtonGroup>
          <ActionButton $primary onClick={() => navigate('/courses', { state: { refresh: true } })}>
            <FiBookOpen size={16} />
            Access my courses
          </ActionButton>
          <ActionButton onClick={() => navigate('/catalog')}>
            <FiGrid size={16} />
            Browse course catalog
          </ActionButton>
          {payment.receiptUrl && (
            <ActionButton onClick={handleDownloadReceipt}>
              <FiDownload size={16} />
              Download receipt
            </ActionButton>
          )}
        </ButtonGroup>
      </PageStack>
    </Container>
  );
}

export default PurchaseConfirmation;
